import React from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, ShieldCheck, Activity, TrendingUp, AlertTriangle, CheckCircle2, Clock, Server, RefreshCw, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { QualityRing, ProgressBar, containerVariants, itemVariants } from './dashboard/DashboardCharts';
import { useDashboardData } from './dashboard/useDashboardData';
export function DashboardPage({
  onNavigate
}: {
  onNavigate?: (view: string) => void;
}) {
  const {
    stats,
    contentStats,
    pipelineLoading,
    strictModeOn,
    handleRefreshPipeline,
    passCount,
    warnCount,
    failCount,
    avgScore,
    totalValidated,
    systemHealth,
    recentActivity
  } = useDashboardData();
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" actions={<ConnectionBadge status="live" data-id="element-3816" />} data-id="element-3815">
      {/* Section 1: Platform Overview */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-3817">
        <motion.div variants={itemVariants} data-id="element-3818">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-4 h-4 text-blue-500" data-id="element-3820" />} data-id="element-3819" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3821">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-4 h-4 text-indigo-500" data-id="element-3823" />} data-id="element-3822" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3824">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-4 h-4 text-rose-500" data-id="element-3826" />} data-id="element-3825" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3827">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-4 h-4 text-purple-500" data-id="element-3829" />} data-id="element-3828" />
        </motion.div>
      </motion.div>

      {/* Section 2: Content & Quality */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-3830">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-3831">
          Content & Kwaliteit
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3832">
          {/* Left: Content Stats */}
          <div className="space-y-4" data-id="element-3833">
            <div className="flex items-center justify-between" data-id="element-3834">
              <div className="flex items-center gap-2" data-id="element-3835">
                <BookOpen className="w-4 h-4 text-blue-500" data-id="element-3836" />
                <span className="text-sm font-medium text-gray-700" data-id="element-3837">
                  Vragenbank
                </span>
              </div>
              <span className="text-lg font-bold text-gray-900" data-id="element-3838">
                {contentStats.totalQuestions.toLocaleString('nl')}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-3839">
              <div className="flex items-center gap-2" data-id="element-3840">
                <Tag className="w-4 h-4 text-purple-500" data-id="element-3841" />
                <span className="text-sm font-medium text-gray-700" data-id="element-3842">
                  Families
                </span>
              </div>
              <span className="text-sm text-gray-600" data-id="element-3843">
                {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}
              </span>
            </div>
            <ProgressBar value={contentStats.coveragePct} color={contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label="Seed dekking" data-id="element-3844" />
            <ProgressBar value={contentStats.visualCoveragePct} color={contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label={`Visuele componenten (${contentStats.usedVisualsCount}/${contentStats.registeredVisuals})`} data-id="element-3845" />
          </div>

          {/* Right: Quality & Validation */}
          <div className="space-y-4" data-id="element-3846">
            <div className="flex items-center justify-between" data-id="element-3847">
              <div className="flex items-center gap-3" data-id="element-3848">
                {avgScore !== null ? <QualityRing score={avgScore} data-id="element-3849" /> : <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" data-id="element-3850" />}
                <div data-id="element-3851">
                  <div className="text-sm font-medium text-gray-700" data-id="element-3852">
                    Kwaliteitsscore
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-3853">
                    alle {contentStats.totalQuestions.toLocaleString('nl')}{' '}
                    vragen
                  </div>
                </div>
              </div>
              <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-3854">
                <RefreshCw className={`w-4 h-4 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-3855" />
              </button>
            </div>

            {pipelineLoading ? <div className="flex gap-4" data-id="element-3856">
                {['Geslaagd', 'Warnings', 'Fouten'].map(l => <div key={l} className="flex-1 bg-gray-50 rounded-lg p-2 animate-pulse" data-id="element-3857">
                    <div className="h-5 bg-gray-200 rounded mb-1" data-id="element-3858" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" data-id="element-3859" />
                  </div>)}
              </div> : passCount !== null ? <>
                <div className="flex gap-3" data-id="element-3860">
                  <div className="flex-1 bg-emerald-50 rounded-lg p-2.5 text-center" data-id="element-3861">
                    <div className="text-lg font-bold text-emerald-700" data-id="element-3862">
                      {passCount}
                    </div>
                    <div className="text-xs text-emerald-600" data-id="element-3863">Geslaagd</div>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg p-2.5 text-center" data-id="element-3864">
                    <div className="text-lg font-bold text-amber-700" data-id="element-3865">
                      {warnCount}
                    </div>
                    <div className="text-xs text-amber-600" data-id="element-3866">Warnings</div>
                  </div>
                  <div className="flex-1 bg-red-50 rounded-lg p-2.5 text-center" data-id="element-3867">
                    <div className="text-lg font-bold text-red-700" data-id="element-3868">
                      {failCount}
                    </div>
                    <div className="text-xs text-red-600" data-id="element-3869">Fouten</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-3870">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-3871" />
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-3872" />
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-3873" />
                </div>
              </> : <div className="text-sm text-gray-400 text-center py-4" data-id="element-3874">
                Validatie wordt geladen...
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Section 3: Activity & System Health */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-3875">
        {/* Recent Activity */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3876">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3877">
            <Clock className="w-4 h-4" data-id="element-3878" /> Recente Activiteit
          </h3>
          <div className="space-y-3" data-id="element-3879">
            {recentActivity.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-3880">
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-3881">
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-3882" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-3883" /> : <Activity className="w-4 h-4" data-id="element-3884" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-3885">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-3886">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-3887">
                    {item.time}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* System Health */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3888">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3889">
            <Server className="w-4 h-4" data-id="element-3890" /> Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-3891">
            <div className="flex items-center justify-between" data-id="element-3892">
              <span className="text-sm text-gray-600" data-id="element-3893">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-3894">
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-3895">
              <span className="text-sm text-gray-600" data-id="element-3896">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-3897">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-3898">
              <div className="flex items-center justify-between mb-1.5" data-id="element-3899">
                <span className="text-sm text-gray-600" data-id="element-3900">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-3901">
                  {systemHealth.storageMb} / 5 MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-3902">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-3903" />
              </div>
            </div>
            <div data-id="element-3904">
              <div className="flex items-center justify-between mb-1.5" data-id="element-3905">
                <span className="text-sm text-gray-600" data-id="element-3906">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-3907">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-3908">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-3909" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Section 4: Quick Insights */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-3910">
        {/* Subscriptions */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3911">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-3912">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-3913">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-3914">
                <div className="flex justify-between text-xs mb-1" data-id="element-3915">
                  <span className="text-gray-600" data-id="element-3916">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-3917">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-3918">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-3919" />
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Top Learners */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3920">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-3921">
            <TrendingUp className="w-3.5 h-3.5" data-id="element-3922" /> Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-3923">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-3924">
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-3925">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3926">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-3927">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-3928">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-3929">Geen leerlingen gevonden</p>}
        </motion.div>

        {/* Calibration Progress */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3930">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-3931">
            <ShieldCheck className="w-3.5 h-3.5" data-id="element-3932" /> Kalibratie
          </h3>
          <div className="space-y-3" data-id="element-3933">
            <div className="flex items-end justify-between" data-id="element-3934">
              <span className="text-2xl font-bold text-gray-900" data-id="element-3935">
                {contentStats.calibration.goldStandardPct}%
              </span>
              <span className="text-xs text-gray-500" data-id="element-3936">
                {contentStats.calibration.familiesAtGoldStandard} /{' '}
                {contentStats.calibration.totalFamilies} goud
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-3937">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${contentStats.calibration.goldStandardPct}%`
            }} transition={{
              duration: 1,
              delay: 0.3
            }} className={`h-full rounded-full ${contentStats.calibration.goldStandardPct >= 80 ? 'bg-emerald-500' : contentStats.calibration.goldStandardPct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-3938" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-3939">
              <div className="flex justify-between" data-id="element-3940">
                <span className="text-gray-500" data-id="element-3941">citoDifficulty</span>
                <span className="font-semibold text-gray-700" data-id="element-3942">
                  {contentStats.calibration.citoDifficultyCoveragePct}%
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3943">
                <span className="text-gray-500" data-id="element-3944">solutionSteps</span>
                <span className="font-semibold text-gray-700" data-id="element-3945">
                  {contentStats.calibration.solutionStepsCoveragePct}%
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </PageLayout>;
}